// apps/client/src/world/instancer.ts — the outdoor scene's instanced batches (rev 7, contract change 13: every InstanceProxy with the
// same key shares one InstancedMesh per sub-mesh of its template), moved out of outdoor.ts with per-instance culling and far models
// (trees and rocks, 2026-10-16, spec §8.1 and §8.7). Each frame the scene's camera picks, per instance:
//   - nothing, when neither it nor (with shadow maps on) its shadow is in view, or it lies past the horizon;
//   - its far model (objectView `farModel`) when it is at least tuning.render.farModelFrom from the camera, or only its shadow shows;
//   - its near model otherwise. A template without a far model draws all of itself, near or far.
// Only the drawn instances are written, packed at the front of each InstancedMesh, so the GPU and renderer.info see just those: a town
// of 260 trees draws the few dozen in view. Contact blobs stay one batch for every instanced kind, all of them.
// With a fade (light and look, 2026-10-16) each drawn instance carries its object's fade cylinder (`aObj`, engine/shader/fade.ts) packed
// beside its matrix, and every part has a hidden ghost pair that draws the instances fading now with the model they are drawn with.
import { tuning } from '@whiskerton/content';
import * as THREE from 'three';
import { blobMaterial } from '../assets/blob';
import { fadeAt } from '../engine/shader/fade';
import { ghostMaterials, patchObject, worldUniforms } from '../engine/shader/worldPatch';
import { inFarModel, type InstanceProxy } from './objectView';

/** Ghosts draw after every opaque and transparent thing in the scene: all depth passes, then all colour passes. */
export const GHOST_DEPTH_ORDER = 900, GHOST_COLOR_ORDER = 901;
/** Makes `mesh` one pass of a faded object's ghost: after everything else, never culled by three, casting nothing, hidden until used. */
export function ghostMesh(mesh: THREE.InstancedMesh | THREE.Mesh, order: number): void {
  mesh.renderOrder = order; mesh.frustumCulled = false; mesh.castShadow = false; mesh.receiveShadow = true;
  mesh.userData.ghost = true;                                     // out of GTAO's prepass (engine/renderer.ts) and the anchored rebase
  mesh.visible = false;
}
/** A proxy's fade cylinder (its whole view's), or null; `memo` lives for one flush, so a view's cylinder is measured once however many
 *  proxies it has. */
export type FadeOf = (p: InstanceProxy, memo: Map<THREE.Object3D, readonly number[] | null>) => readonly number[] | null;
const FADE = tuning.light.fade;
const NO_OBJ = [0, 0, 0, 0] as const;
/** A faded object's ghost pair (engine/shader/worldPatch.ts ghostMaterials): drawn after everything opaque, its depth pass then its colour. */
type Ghost = { depth: THREE.InstancedMesh; color: THREE.InstancedMesh; geo: THREE.BufferGeometry; obj: THREE.InstancedBufferAttribute; capacity: number };

type Lod = 'all' | 'near' | 'far';
/** `jitter`: a mesh's `userData.tintJitter`; each instance's colour then varies by up to that much (brightness, a little warmer or
 *  cooler), fixed by where it stands, so a stand of one kind is not a row of clones. */
/** With a fade, `geo` is the part's own copy of the template geometry carrying its `obj` (the kit's or builder's geometry is shared and
 *  never gets it): the drawn instances' fade cylinders, packed like its matrices. */
type Part = { geometry: THREE.BufferGeometry; material: THREE.Material; local: THREE.Matrix4; castShadow: boolean; lod: Lod; jitter: number;
  wind?: readonly [number, number, number]; mesh: THREE.InstancedMesh | null;
  geo: THREE.BufferGeometry | null; obj: THREE.InstancedBufferAttribute | null; ghost: Ghost | null };
/** Per instance: 0 not drawn, 1 near model, 2 far model. */
const HIDDEN = 0, NEAR = 1, FAR = 2;
type Batch = {
  parts: Part[]; blobs: THREE.Matrix4[]; proxies: InstanceProxy[]; index: Map<InstanceProxy, number>; capacity: number;
  bounds: THREE.Sphere;          // both models, in the template's space
  height: number;                // the template's top above its base (the reach of its shadow)
  hasFar: boolean;
  world: THREE.Matrix4[]; on: Uint8Array; state: Uint8Array;   // per slot: world matrix, shown, what is drawn
  stale: boolean;                // matrices changed since the parts were last written
  cyl: Float32Array | null;      // with a fade, per slot: its fade cylinder (x, z, −radius, top), zero when hidden
  members: number[];             // the slots fading now
};
const ZERO = new THREE.Matrix4().makeScale(0, 0, 0);
/** Every contact blob of every instanced kind is this unit square, scaled to its size by its instance matrix: one draw for them all
 *  (spec §8.7, M2 playtest: a blob batch per kind cost a dozen draws for flat shadows). */
const UNIT_BLOB = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);

function partsOf(template: THREE.Object3D): Pick<Batch, 'parts' | 'blobs' | 'bounds' | 'height' | 'hasFar'> {
  template.updateMatrixWorld(true);
  const inv = template.matrixWorld.clone().invert(), parts: Part[] = [], blobs: THREE.Matrix4[] = [], box = new THREE.Box3();
  const anyFar = (() => { let f = false; template.traverse((o) => { if (o.userData.lod === 'far') f = true; }); return f; })();
  template.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || Array.isArray(m.material)) return;
    const local = new THREE.Matrix4().multiplyMatrices(inv, m.matrixWorld);
    if (m.userData.blob && m.material === blobMaterial()) {         // a contactBlob(size): a size × size square on the ground
      m.geometry.computeBoundingBox();
      const b = m.geometry.boundingBox!, c = b.getCenter(new THREE.Vector3());
      blobs.push(local.multiply(new THREE.Matrix4().makeTranslation(c.x, c.y, c.z)).multiply(new THREE.Matrix4().makeScale(b.max.x - b.min.x, 1, b.max.z - b.min.z)));
      return;
    }
    if (!m.geometry.boundingBox) m.geometry.computeBoundingBox();
    if (!m.userData.blob) box.union(m.geometry.boundingBox!.clone().applyMatrix4(local));
    parts.push({ geometry: m.geometry, material: m.material, local, castShadow: !m.userData.blob, mesh: null, jitter: Number(m.userData.tintJitter) || 0,
      lod: !anyFar ? 'all' : inFarModel(m, template) ? 'far' : 'near', wind: template.userData.wind as readonly [number, number, number] | undefined,
      geo: null, obj: null, ghost: null });
  });
  if (box.isEmpty()) box.set(new THREE.Vector3(-0.5, 0, -0.5), new THREE.Vector3(0.5, 1, 0.5));
  return { parts, blobs, bounds: box.getBoundingSphere(new THREE.Sphere()), height: Math.max(0.1, box.max.y), hasFar: anyFar };
}
const shown = (o: THREE.Object3D | null): boolean => { for (; o; o = o.parent) if (!o.visible) return false; return true; };
/** The state of instance a part draws (−1: any shown one), and whether it draws an instance in state `s`. */
const wantOf = (part: Part) => (part.lod === 'near' ? NEAR : part.lod === 'far' ? FAR : -1);
const draws = (want: number, s: number) => s !== HIDDEN && (want === -1 || s === want);
/** A stable 0…1 from a place on the ground. */
const noise = (x: number, z: number, salt: number) => { const v = Math.sin(x * 12.9898 + z * 78.233 + salt * 37.719) * 43758.5453; return v - Math.floor(v); };
/** The tint of an instance standing at (x, z): brightness within ±jitter, red and blue nudged opposite ways (warmer or cooler). */
function tintAt(x: number, z: number, jitter: number, out: THREE.Color): THREE.Color {
  const k = 1 + jitter * (2 * noise(x, z, 1) - 1), warm = jitter * 0.5 * (2 * noise(x, z, 2) - 1);
  return out.setRGB(k * (1 + warm), k, k * (1 - warm));
}

/** What the last frame's camera saw, for instances filed between frames. */
interface View { frustum: THREE.Frustum; eye: THREE.Vector3; shadows: boolean; key: number[] }

export class Instancer {
  private readonly root: THREE.Group;
  private readonly batches = new Map<string, Batch>();
  private readonly dirty = new Set<Batch>();
  private blobMesh: THREE.InstancedMesh | null = null;
  private seen: View | null = null;
  private readonly sunDir = new THREE.Vector3(0, 1, 0);
  private readonly fadeOf: FadeOf | null;
  /** `fadeOf` (the town; null for the title's showcase): each instance's fade cylinder, carried to the shader, and ghosts for the fading. */
  constructor(root: THREE.Group, fadeOf: FadeOf | null = null) { this.root = root; this.fadeOf = fadeOf; }
  add(p: InstanceProxy): void {
    let b = this.batches.get(p.key);
    if (!b) {
      b = { ...partsOf(p.template), proxies: [], index: new Map(), capacity: 0, world: [], on: new Uint8Array(0), state: new Uint8Array(0), stale: true,
        cyl: null, members: [] };
      this.batches.set(p.key, b);
    }
    if (b.index.has(p)) { this.dirty.add(b); return; }
    b.index.set(p, b.proxies.length);
    b.proxies.push(p);
    if (b.proxies.length > b.capacity) this.grow(b, Math.max(16, b.capacity * 2));
    this.dirty.add(b);
  }
  remove(p: InstanceProxy): void {
    const b = this.batches.get(p.key);
    const i = b?.index.get(p);
    if (!b || i === undefined) return;
    const last = b.proxies.pop()!;                                // swap-remove: the last proxy takes the freed slot
    if (last !== p) { b.proxies[i] = last; b.index.set(last, i); }
    b.index.delete(p);
    this.dirty.add(b);
  }
  /** A proxy moved or changed visibility; one a view added during `update` joins its batch here. */
  touch(p: InstanceProxy): void { this.add(p); }
  get isDirty(): boolean { return this.dirty.size > 0; }
  /** The sun's direction (toward the sun), for how far shadows reach (OutdoorScene.tick passes the clock lighting's). */
  sun(dir: THREE.Vector3): void { this.sunDir.copy(dir); }
  /** Re-reads the moved, added, removed and hidden instances, and draws them for the last view (all near before any view). */
  flush(): void {
    const memo = new Map<THREE.Object3D, readonly number[] | null>();
    let blobsDirty = false;
    for (const b of this.dirty) {
      if (b.blobs.length > 0) blobsDirty = true;
      b.proxies.forEach((p, i) => {
        p.updateWorldMatrix(true, false);
        b.world[i]!.copy(p.matrixWorld);
        b.on[i] = shown(p) ? 1 : 0;
        if (b.cyl) b.cyl.set((b.on[i] && this.fadeOf!(p, memo)) || NO_OBJ, i * 4);
      });
      b.stale = true;
      this.apply(b);
    }
    this.dirty.clear();
    if (blobsDirty) this.flushBlobs();
  }
  /** Called with the camera of every render of the scene (its onBeforeRender): culls and picks near or far models for it. A view the
   *  same as the last one changes nothing. `shadows`: the renderer draws shadow maps (instances out of view may shade what is in it). */
  view(camera: THREE.Camera, shadows: boolean): void {
    if (this.dirty.size > 0) this.flush();
    const key = [...camera.matrixWorld.elements, ...camera.projectionMatrix.elements, shadows ? 1 : 0, this.sunDir.x, this.sunDir.y, this.sunDir.z,
      worldUniforms.uCamXZ.value.x, worldUniforms.uCamXZ.value.y, worldUniforms.uCurve.value];
    if (this.seen && this.seen.key.length === key.length && this.seen.key.every((v, i) => v === key[i])) {
      for (const b of this.batches.values()) if (b.stale) this.apply(b);
      return;
    }
    const frustum = this.seen?.frustum ?? new THREE.Frustum(), eye = this.seen?.eye ?? new THREE.Vector3();
    frustum.setFromProjectionMatrix(new THREE.Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse));
    eye.setFromMatrixPosition(camera.matrixWorld);
    this.seen = { frustum, eye, shadows, key };
    for (const b of this.batches.values()) this.apply(b);
  }
  /** Picks what each of `b`'s instances draws for the last view and rewrites its parts when any of that changed (or `b` is stale). */
  private apply(b: Batch): void {
    let changed = b.stale;
    for (let i = 0; i < b.proxies.length; i++) {
      const s = this.stateOf(b, i);
      if (s !== b.state[i]) { b.state[i] = s; changed = true; }
    }
    if (!changed) return;
    b.stale = false;
    const m = new THREE.Matrix4(), tint = new THREE.Color();
    for (const part of b.parts) {
      const mesh = part.mesh!, want = wantOf(part), obj = part.obj?.array as Float32Array | undefined;
      let k = 0;
      for (let i = 0; i < b.proxies.length; i++) {
        if (!draws(want, b.state[i]!)) continue;
        const w = b.world[i]!;
        if (part.jitter > 0) mesh.setColorAt(k, tintAt(w.elements[12]!, w.elements[14]!, part.jitter, tint));
        if (obj) obj.set(b.cyl!.subarray(i * 4, i * 4 + 4), k * 4);
        mesh.setMatrixAt(k++, m.multiplyMatrices(w, part.local));
      }
      mesh.count = k;
      mesh.visible = k > 0;
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      if (part.obj) part.obj.needsUpdate = true;
    }
    if (b.members.length > 0) this.ghosts(b);                    // a fading instance moved, hid, or changed model
  }
  /** Which instances fade now (the CPU's wider test, engine/shader/fade.ts), and their ghosts. OutdoorScene.tick calls it every frame. */
  fade(viewers: readonly THREE.Vector4[], cam: THREE.Vector3): void {
    for (const b of this.batches.values()) {
      if (!b.cyl) continue;
      const was = b.members.length;
      b.members.length = 0;
      for (let i = 0; i < b.proxies.length; i++) if (fadeAt(b.cyl, i * 4, viewers, cam, FADE.margin) > 0) b.members.push(i);
      if (was === 0 && b.members.length === 0) continue;
      this.ghosts(b);
    }
  }
  /** Writes each part's ghost pair: the fading instances that part draws, with their matrices, cylinders and tints. */
  private ghosts(b: Batch): void {
    const m = new THREE.Matrix4(), tint = new THREE.Color();
    for (const part of b.parts) {
      if (!part.ghost) continue;
      const want = wantOf(part);
      let n = 0;
      for (const i of b.members) if (i < b.proxies.length && draws(want, b.state[i]!)) n++;
      let g = part.ghost;
      if (n === 0) { g.depth.visible = g.color.visible = false; g.depth.count = g.color.count = 0; continue; }
      if (n > g.capacity) g = this.makeGhost(part, n * 2);
      const gobj = g.obj.array as Float32Array;
      let k = 0;
      for (const i of b.members) {
        if (i >= b.proxies.length || !draws(want, b.state[i]!)) continue;
        const w = b.world[i]!;
        if (part.jitter > 0) g.depth.setColorAt(k, tintAt(w.elements[12]!, w.elements[14]!, part.jitter, tint));
        gobj.set(b.cyl!.subarray(i * 4, i * 4 + 4), k * 4);
        g.depth.setMatrixAt(k++, m.multiplyMatrices(w, part.local));
      }
      g.depth.count = g.color.count = n;
      g.depth.instanceMatrix.needsUpdate = true;
      if (g.depth.instanceColor) g.depth.instanceColor.needsUpdate = true;
      g.obj.needsUpdate = true;
      g.depth.visible = g.color.visible = true;
    }
  }
  /** A part's ghost pair with room for `capacity` instances: its own copy of the geometry, so its aObj holds only the fading ones. */
  private makeGhost(part: Part, capacity: number): Ghost {
    this.dropGhost(part);
    const geo = part.geometry.clone(), obj = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4);
    geo.setAttribute('aObj', obj);
    const mats = ghostMaterials(part.mesh!.material as THREE.Material);
    const depth = new THREE.InstancedMesh(geo, mats.depth, capacity), color = new THREE.InstancedMesh(geo, mats.color, capacity);
    color.instanceMatrix = depth.instanceMatrix;
    // Tinted as its instance (made now, like the batch's, so the warm-up compiles the variant that reads it).
    if (part.jitter > 0) depth.instanceColor = color.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3).fill(1), 3);
    ghostMesh(depth, GHOST_DEPTH_ORDER); ghostMesh(color, GHOST_COLOR_ORDER);
    depth.count = color.count = 0;
    this.root.add(depth, color);
    return (part.ghost = { depth, color, geo, obj, capacity });
  }
  private dropGhost(part: Part): void {
    const g = part.ghost;
    if (!g) return;
    for (const m of [g.depth, g.color]) { m.removeFromParent(); m.dispose(); }
    g.geo.dispose();
    part.ghost = null;
  }
  private readonly c = new THREE.Vector3();
  private readonly ball = new THREE.Sphere();
  private stateOf(b: Batch, i: number): number {
    if (!b.on[i]) return HIDDEN;
    const v = this.seen;
    if (!v) return NEAR;
    const w = b.world[i]!, cam = worldUniforms.uCamXZ.value, e = w.elements;
    if (Math.hypot(e[12]! - cam.x, e[14]! - cam.y) > tuning.camera.horizon) return HIDDEN;   // the world shader drops it anyway
    const scale = w.getMaxScaleOnAxis(), r = b.bounds.radius * scale + tuning.render.cullMargin;
    const c = this.c.copy(b.bounds.center).applyMatrix4(w);
    const ground = e[13]!, bend = (x: number, z: number) => ((x - cam.x) ** 2 + (z - cam.y) ** 2) * worldUniforms.uCurve.value;
    if (v.frustum.intersectsSphere(this.ball.set(c.setY(c.y - bend(c.x, c.z)), r))) {
      if (!b.hasFar) return NEAR;
      const d = c.distanceTo(v.eye), R = tuning.render;
      return b.state[i] === FAR ? (d > R.farModelFrom - R.farModelBand ? FAR : NEAR) : (d > R.farModelFrom + R.farModelBand ? FAR : NEAR);
    }
    if (!v.shadows) return HIDDEN;
    // Out of view: its shadow on the ground runs from its base away from the sun, as far as the sun throws its top.
    const sun = this.sunDir, flat = Math.hypot(sun.x, sun.z);
    if (sun.y <= 0 || flat < 1e-3) return HIDDEN;
    const reach = Math.min(tuning.render.shadowReachMax, (b.height * scale * flat) / Math.max(sun.y, 0.05));
    const steps = Math.max(1, Math.ceil(reach / Math.max(0.5, r))), bx = e[12]!, bz = e[14]!;
    for (let k = 1; k <= steps; k++) {
      const t = (reach * k) / steps, x = bx - (sun.x / flat) * t, z = bz - (sun.z / flat) * t;
      if (v.frustum.intersectsSphere(this.ball.set(this.c.set(x, ground - bend(x, z), z), r))) return b.hasFar ? FAR : NEAR;
    }
    return HIDDEN;
  }
  /** Rewrites the one blob batch from every kind's proxies (a change to any of them moves the slots after it). */
  private flushBlobs(): void {
    let n = 0;
    for (const b of this.batches.values()) n += b.blobs.length * b.proxies.length;
    let mesh = this.blobMesh;
    if (!mesh || mesh.instanceMatrix.count < n) {
      mesh?.removeFromParent(); mesh?.dispose();
      mesh = new THREE.InstancedMesh(UNIT_BLOB, blobMaterial(), Math.max(64, mesh ? mesh.instanceMatrix.count * 2 : 0, n));
      mesh.castShadow = false;
      mesh.receiveShadow = true;
      mesh.userData.blob = true;                                   // out of the GTAO prepass (engine/renderer.ts)
      patchObject(mesh);
      this.root.add(mesh);
      this.blobMesh = mesh;
    }
    const m = new THREE.Matrix4();
    let i = 0;
    for (const b of this.batches.values()) for (let k = 0; k < b.proxies.length; k++) {
      for (const local of b.blobs) mesh.setMatrixAt(i++, b.on[k] ? m.multiplyMatrices(b.world[k]!, local) : ZERO);
    }
    mesh.count = i;
    mesh.instanceMatrix.needsUpdate = true;
    mesh.computeBoundingSphere();
  }
  clear(): void {
    for (const b of this.batches.values()) for (const part of b.parts) {
      part.mesh?.removeFromParent(); part.mesh?.dispose(); part.geo?.dispose(); this.dropGhost(part);
    }
    this.blobMesh?.removeFromParent(); this.blobMesh?.dispose(); this.blobMesh = null;
    this.batches.clear();
    this.dirty.clear();
    this.seen = null;
  }
  private grow(b: Batch, capacity: number): void {
    b.capacity = capacity;
    while (b.world.length < capacity) b.world.push(new THREE.Matrix4());
    const on = new Uint8Array(capacity), state = new Uint8Array(capacity);
    on.set(b.on); state.set(b.state);
    b.on = on; b.state = state;
    if (this.fadeOf) { const cyl = new Float32Array(capacity * 4); if (b.cyl) cyl.set(b.cyl); b.cyl = cyl; }
    for (const part of b.parts) {
      part.mesh?.removeFromParent();
      part.mesh?.dispose();
      let geo = part.geometry;
      if (this.fadeOf) {
        part.geo ??= part.geometry.clone();
        part.geo.dispose();                                        // frees the old aObj's buffer; the copy uploads again with the new one
        part.obj = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4);
        part.geo.setAttribute('aObj', part.obj);
        geo = part.geo;
      }
      const mesh = new THREE.InstancedMesh(geo, part.material, capacity);
      // Made with the mesh, not on its first tint: the shader that reads it is compiled before the first frame (compileAsync).
      if (part.jitter > 0) mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3).fill(1), 3);
      mesh.castShadow = part.castShadow;
      mesh.receiveShadow = true;
      mesh.userData.blob = !part.castShadow;                      // contact blobs stay out of the GTAO prepass (engine/renderer.ts)
      mesh.count = 0;
      mesh.visible = false;
      // Culled here, per instance, at the curved world's bent positions: three's own test of the whole batch's bounds would only
      // disagree with it (and cost a bounding-sphere pass over every instance on each write).
      mesh.frustumCulled = false;
      patchObject(mesh, { wind: part.wind });
      this.root.add(mesh);
      part.mesh = mesh;
      // Made now, hidden, so the town's shader warm-up (Game.ready) compiles the ghost programs too: no hitch at the first fade.
      if (this.fadeOf) this.makeGhost(part, part.ghost?.capacity ?? 8);
    }
    b.stale = true;
  }
}
